import type { Command, PushSubscriptionInfo } from "./types";

const DB = "rainmachine-auth";
const STORE = "credentials";
const STATE_STORE = "read-state";

type CachedState<T> = { value: T; etag: string; cachedAt: string };

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 2);
    request.onupgradeneeded = () => {
			if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
			if (!request.result.objectStoreNames.contains(STATE_STORE)) request.result.createObjectStore(STATE_STORE);
		};
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function cachedState<T>(key: string): Promise<CachedState<T> | null> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const request = db.transaction(STATE_STORE, "readonly").objectStore(STATE_STORE).get(key);
		request.onsuccess = () => resolve((request.result as CachedState<T> | undefined) || null);
		request.onerror = () => reject(request.error);
	});
}

async function cacheState<T>(key: string, value: T, etag: string): Promise<void> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const transaction = db.transaction(STATE_STORE, "readwrite");
		transaction.objectStore(STATE_STORE).put({ value, etag, cachedAt: new Date().toISOString() }, key);
		transaction.oncomplete = () => resolve();
		transaction.onerror = () => reject(transaction.error);
	});
}

async function storedValue(key: string): Promise<string> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, "readonly");
    const request = transaction.objectStore(STORE).get(key);
    request.onsuccess = () => resolve((request.result as string | undefined) || "");
    request.onerror = () => reject(request.error);
  });
}

async function setStoredValue(key: string, value: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).put(value, key);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

async function deleteStoredValue(key: string): Promise<void> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const transaction = db.transaction(STORE, "readwrite");
		transaction.objectStore(STORE).delete(key);
		transaction.oncomplete = () => resolve();
		transaction.onerror = () => reject(transaction.error);
	});
}

export async function credentials() {
  return { token: await storedValue("token"), deviceName: await storedValue("deviceName") };
}

export async function saveCredentials(token: string, deviceName: string) {
  await Promise.all([setStoredValue("token", token), setStoredValue("deviceName", deviceName)]);
	const saved = await credentials();
	if (saved.token !== token || saved.deviceName !== deviceName) throw new Error("This browser did not retain the device pairing");
}

export async function forgetCredentials() {
  await Promise.all([setStoredValue("token", ""), setStoredValue("deviceName", "")]);
}

export class APIError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
	return (await request<T>(path, options)).value;
}

export async function apiWithETag<T>(path: string, options: RequestInit = {}): Promise<{ value: T; etag: string }> {
	const result = await request<T>(path, options);
	return { value: result.value, etag: result.response.headers.get("ETag") || "" };
}

export async function apiWithMetadata<T>(path: string, options: RequestInit = {}): Promise<{ value: T; cachedAt: string }> {
	const result = await request<T>(path, options);
	return { value: result.value, cachedAt: result.response.headers.get("X-Cached-At") || "" };
}

async function request<T>(path: string, options: RequestInit = {}): Promise<{ value: T; response: Response }> {
  const { token, deviceName } = await credentials();
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (deviceName) headers.set("X-Device-Name", deviceName);
	if (options.method && options.method !== "GET") {
		const csrf = document.cookie.split("; ").find((item) => item.startsWith("rm_csrf="))?.split("=").slice(1).join("=");
		if (csrf) headers.set("X-CSRF-Token", decodeURIComponent(csrf));
	}
	let response: Response;
	try {
		response = await fetch(path, { ...options, headers, credentials: "same-origin" });
	} catch (reason) {
		if ((options.method || "GET") !== "GET") throw reason;
		const cached = await cachedState<T>(path);
		if (!cached) throw reason;
		let value = cached.value;
		if (path === "/api/v1/controllers" && value && typeof value === "object" && "items" in value) {
			const record = value as { items: Array<Record<string, unknown>> };
			value = { ...record, items: record.items.map((item) => ({ ...item, online: false, stale: true })) } as T;
		}
		return { value, response: new Response(null, { headers: { ETag: cached.etag, "X-Cached-At": cached.cachedAt } }) };
	}
  if (!response.ok) {
    const value = await response.json().catch(() => null);
    throw new APIError(value?.error?.message || `Request failed (${response.status})`, response.status);
  }
	if (response.status === 204) return { value: undefined as T, response };
	const value = await response.json() as T;
	if ((options.method || "GET") === "GET") void cacheState(path, value, response.headers.get("ETag") || "");
	return { value, response };
}

type PasskeyOptions = { challengeId: string; options: { publicKey: PublicKeyCredentialCreationOptions | PublicKeyCredentialRequestOptions } };
const decode64 = (value: string) => Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=")), (character) => character.charCodeAt(0));
const encode64 = (value: ArrayBuffer | null) => value === null ? null : btoa(String.fromCharCode(...new Uint8Array(value))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export async function registerPasskey(name: string, displayName: string, recoveryCode: string) {
	const started = await api<PasskeyOptions>("/api/v1/auth/passkeys/register/options", { method: "POST", body: JSON.stringify({ name, displayName, recoveryCode }) });
	const publicKey = started.options.publicKey as PublicKeyCredentialCreationOptions;
	publicKey.challenge = decode64(publicKey.challenge as unknown as string);
	publicKey.user = { ...publicKey.user, id: decode64(publicKey.user.id as unknown as string) };
	publicKey.excludeCredentials = publicKey.excludeCredentials?.map((item) => ({ ...item, id: decode64(item.id as unknown as string) }));
	const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential | null;
	if (!credential) throw new Error("Passkey creation was cancelled");
	const response = credential.response as AuthenticatorAttestationResponse;
	return api("/api/v1/auth/passkeys/register/verify", { method: "POST", headers: { "X-Passkey-Challenge": started.challengeId, "X-Recovery-Code": recoveryCode }, body: JSON.stringify({ id: credential.id, rawId: encode64(credential.rawId), type: credential.type, response: { attestationObject: encode64(response.attestationObject), clientDataJSON: encode64(response.clientDataJSON), transports: response.getTransports?.() || [] }, clientExtensionResults: credential.getClientExtensionResults(), authenticatorAttachment: credential.authenticatorAttachment }) });
}

export async function loginPasskey(name: string) {
	const started = await api<PasskeyOptions>("/api/v1/auth/passkeys/login/options", { method: "POST", body: JSON.stringify({ name }) });
	const publicKey = started.options.publicKey as PublicKeyCredentialRequestOptions;
	publicKey.challenge = decode64(publicKey.challenge as unknown as string);
	publicKey.allowCredentials = publicKey.allowCredentials?.map((item) => ({ ...item, id: decode64(item.id as unknown as string) }));
	const credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential | null;
	if (!credential) throw new Error("Passkey login was cancelled");
	const response = credential.response as AuthenticatorAssertionResponse;
	return api("/api/v1/auth/passkeys/login/verify", { method: "POST", headers: { "X-Passkey-Challenge": started.challengeId }, body: JSON.stringify({ id: credential.id, rawId: encode64(credential.rawId), type: credential.type, response: { authenticatorData: encode64(response.authenticatorData), clientDataJSON: encode64(response.clientDataJSON), signature: encode64(response.signature), userHandle: encode64(response.userHandle) }, clientExtensionResults: credential.getClientExtensionResults(), authenticatorAttachment: credential.authenticatorAttachment }) });
}

export async function enablePush(deviceName: string) {
	if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("Web Push is unavailable in this browser");
	const permission = await Notification.requestPermission(); if (permission !== "granted") throw new Error("Notification permission was not granted");
	const registration = await navigator.serviceWorker.ready;
	const { publicKey } = await api<{ publicKey: string }>("/api/v1/push/vapid-key");
	const subscription = await registration.pushManager.getSubscription() || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decode64(publicKey) });
	const json = subscription.toJSON();
	return api<PushSubscriptionInfo>("/api/v1/push-subscriptions", { method: "POST", body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, deviceName, preferences: { controllerOffline: true, controllerRecovery: true, commandFailure: true, programEvents: true, manualProgramEvents: false, zoneEvents: false } }) });
}

export type DevicePushState = { supported: boolean; permission: NotificationPermission | "unsupported"; subscription?: PushSubscriptionInfo; browserSubscribed: boolean };

export async function devicePushState(): Promise<DevicePushState> {
	if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return { supported: false, permission: "unsupported", browserSubscribed: false };
	const registration = await navigator.serviceWorker.ready;
	const browserSubscription = await registration.pushManager.getSubscription();
	if (!browserSubscription) return { supported: true, permission: Notification.permission, browserSubscribed: false };
	const endpointHash = await sha256Text(browserSubscription.endpoint);
	const result = await api<{ items: PushSubscriptionInfo[] }>("/api/v1/push-subscriptions");
	return { supported: true, permission: Notification.permission, browserSubscribed: true, subscription: result.items.find((item) => item.endpointHash === endpointHash) };
}

export async function testPushSubscription(id: string) {
	return api<{ delivered: boolean }>(`/api/v1/push-subscriptions/${id}/test`, { method: "POST" });
}

async function sha256Text(value: string): Promise<string> {
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
	return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function idempotencyKey() {
	if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();

	const bytes = new Uint8Array(16);
	if (typeof globalThis.crypto?.getRandomValues === "function") {
		globalThis.crypto.getRandomValues(bytes);
	} else {
		// Idempotency keys need uniqueness, not secrecy. This branch supports very old
		// browsers that expose neither Web Crypto method.
		for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
	}
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
	return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

export async function mutate<T>(path: string, method: "POST" | "PUT" | "DELETE", body?: unknown, ifMatch?: string): Promise<T> {
	const encodedBody = body === undefined ? "" : JSON.stringify(body);
	const mutationKey = `mutation:${await mutationFingerprint(method, path, encodedBody, ifMatch || "")}`;
	const commandReferenceKey = `${mutationKey}:command`;
	let key = await storedValue(mutationKey);
	const priorCommandID = await storedValue(commandReferenceKey);
	if (key && priorCommandID) {
		try {
			const prior = await api<Command>(`/api/v1/commands/${priorCommandID}`);
			if (commandTerminal(prior)) {
				await Promise.all([deleteStoredValue(mutationKey), deleteStoredValue(commandReferenceKey), deleteStoredValue(`command:${priorCommandID}`)]);
				key = "";
			}
		} catch (reason) {
			// Preserve the original key when command state cannot be confirmed. The
			// server will safely return the original command instead of replaying it.
			if (reason instanceof APIError && reason.status === 404) {
				await Promise.all([deleteStoredValue(mutationKey), deleteStoredValue(commandReferenceKey), deleteStoredValue(`command:${priorCommandID}`)]);
				key = "";
			}
		}
	}
	if (!key) {
		key = idempotencyKey();
		await setStoredValue(mutationKey, key);
	}
	const headers: Record<string, string> = { "Idempotency-Key": key };
	if (ifMatch) headers["If-Match"] = ifMatch;
	const result = await api<T>(path, { method, headers, body: encodedBody || undefined });
	if (result && typeof result === "object" && "id" in result) {
		const commandID = String((result as { id: unknown }).id);
		await Promise.all([setStoredValue(`command:${commandID}`, mutationKey), setStoredValue(commandReferenceKey, commandID)]);
	}
	return result;
}

export function commandTerminal(command: Command): boolean {
	return !["pending", "running"].includes(command.status);
}

export async function waitForCommand(command: Command, onUpdate: (value: Command) => void, signal?: AbortSignal): Promise<Command> {
	let current = command;
	let delay = 750;
	const deadline = Date.now() + 4 * 60 * 1000;
	onUpdate(current);
	while (!commandTerminal(current) && Date.now() < deadline) {
		await commandPollDelay(delay, signal);
		try {
			current = await api<Command>(`/api/v1/commands/${command.id}`, signal ? { signal } : {});
			onUpdate(current);
			delay = Math.min(3000, Math.round(delay * 1.35));
		} catch (reason) {
			if (signal?.aborted) throw reason;
			if (reason instanceof APIError && reason.status < 500 && reason.status !== 429) throw reason;
			delay = Math.min(5000, Math.round(delay * 1.5));
		}
	}
	if (commandTerminal(current)) {
		const mutationKey = await storedValue(`command:${command.id}`);
		if (mutationKey) await Promise.all([deleteStoredValue(mutationKey), deleteStoredValue(`${mutationKey}:command`)]);
		await deleteStoredValue(`command:${command.id}`);
	}
	return current;
}

export async function mutationFingerprint(method: string, path: string, body: string, ifMatch: string): Promise<string> {
	const value = new TextEncoder().encode(`${method}\n${path}\n${ifMatch}\n${body}`);
	if (globalThis.crypto?.subtle) {
		const digest = await globalThis.crypto.subtle.digest("SHA-256", value);
		return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
	}
	let first = 2166136261;
	let second = 2246822519;
	for (const byte of value) {
		first = Math.imul(first ^ byte, 16777619);
		second = Math.imul(second ^ byte, 3266489917);
	}
	return `${(first >>> 0).toString(16).padStart(8, "0")}${(second >>> 0).toString(16).padStart(8, "0")}`;
}

function commandPollDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal?.reason || new DOMException("Command polling was cancelled", "AbortError"));
			return;
		}
		const onAbort = () => {
			clearTimeout(timer);
			reject(signal?.reason || new DOMException("Command polling was cancelled", "AbortError"));
		};
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, milliseconds);
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}
